import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Paper, Typography, Button, IconButton, Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TimerIcon from "@mui/icons-material/Timer";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { ManagedUploadArea } from "../../Billing/ManagedUploadArea";

import { ONBOARDING_ROOT } from "../../../backend/sagas/onboardingRedirect";
import { getWorkspaceRole, hasPermission } from "../../../utils/permissions";
import { useNewFrameShown } from "../../../next/shell/useNewFrameShown";
import { useNextFrameMounted } from "../../../next/shell/frameMounted";

const TrialWidget: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const billing = useSelector((state: any) => state.billingStore);
    const currentUser = useSelector((state: any) => state.authStore.currentUser);
    const [dismissed, setDismissed] = useState(false);
    // Never in the new frame: its pages have their own trial line (walkthrough 2026-10-29, issue 1).
    const inNextFrame = useNextFrameMounted();
    const newFrameShown = useNewFrameShown();
    const [timeLeft, setTimeLeft] = useState("");

    // The backend now 403s the managed-upload endpoint for anyone below admin
    // (see POST /api/auth/billing/upload-managed-file), so mirror the same
    // isAdminOrAbove gate used in SettingsPage here to keep the widget from
    // offering an affordance that will fail for non-admin roles.
    const isAdminOrAbove = currentUser?.role === "admin" || currentUser?.role === "super_admin";

    const isPreTrial = billing.trialEndsAt === null
        && !billing.hasActiveSubscription
        && billing.isSuperAdminWorkspace === false;

    const isActiveTrial = billing.isTrialActive === true
        && billing.isSuperAdminWorkspace === false
        && !billing.hasActiveSubscription;

    // Not during registration. The widget is mounted app-wide, so the moment
    // someone signs back in mid-signup it floats over the flow asking them to
    // pick a plan — in the middle of the one journey that is not about money.
    // Owner, 2026-09-11.
    const inSignupFlow = pathname.startsWith(ONBOARDING_ROOT);

    // A volunteer has no workspace to pay for: their whole app is
    // /volunteering, and there is nothing on offer here for them to buy.
    // Owner: "if someone just wants to volunteer, there obviously shouldn't be
    // a payment requirement."
    const isVolunteer = getWorkspaceRole(currentUser) === "volunteer";

    // Only show when: user is authenticated, billing is enabled, and widget is not dismissed
    const shouldShow = !!currentUser
        && billing.billingEnabled
        && (isPreTrial || isActiveTrial)
        && !inSignupFlow
        && !isVolunteer
        // Only people who can see billing: a team member has no plan to pick (walkthrough issue 1).
        && hasPermission("read_billing", currentUser)
        && !inNextFrame
        && !newFrameShown
        && !dismissed;

    useEffect(() => {
        if (!billing.trialEndsAt) return;

        const updateCountdown = () => {
            const now = new Date().getTime();
            const end = new Date(billing.trialEndsAt).getTime();
            const diff = end - now;

            if (diff <= 0) {
                setTimeLeft("Expired");
                return;
            }

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

            if (hours > 24) {
                const days = Math.floor(hours / 24);
                setTimeLeft(`${days}d ${hours % 24}h remaining`);
            } else {
                setTimeLeft(`${hours}h ${minutes}m remaining`);
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 60000); // Update every minute

        return () => clearInterval(interval);
    }, [billing.trialEndsAt]);

    if (!shouldShow) return null;

    return (
        <Paper
            className="cy-trial-widget"
            elevation={6}
            sx={{
                position: "fixed",
                bottom: 24,
                right: 24,
                zIndex: 1200,
                width: 320,
                borderRadius: 2,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    background: "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)",
                    color: "white",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isPreTrial ? (
                        <UploadFileIcon fontSize="small" />
                    ) : (
                        <TimerIcon fontSize="small" />
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {isPreTrial ? "Get Started" : (billing.isNewUser === false ? "ConnectYid has been updated!" : "Free Trial")}
                    </Typography>
                </Box>
                <IconButton size="small" aria-label={t("legal_docs.close")} onClick={() => setDismissed(true)} sx={{ color: "white" }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ p: 2 }}>
                {isPreTrial ? (
                    <>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Upload your households to start.
                        </Typography>
                        <Button
                            variant="contained"
                            fullWidth
                            size="small"
                            onClick={() => navigate("/households?open=import&currentPage=1&perPage=25")}
                            sx={{ mb: 1 }}
                        >
                            Import Households
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {timeLeft}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Explore all features during your trial period.
                        </Typography>
                        <Button
                            variant="contained"
                            fullWidth
                            size="small"
                            onClick={() => navigate("/pricing")}
                            sx={{ mb: 1 }}
                        >
                            Select a Plan
                        </Button>
                        {isAdminOrAbove && billing.managedUploadRequested && !billing.managedUploadFileUploaded && (
                            <Box sx={{ mt: 1 }}>
                                <ManagedUploadArea />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default TrialWidget;
